import { TypedDocumentNode, gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  GameStatus,
  Position,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { playerGameStatusLabels } from '@sorare/core/src/lib/glossary';
import { withFragments } from '@sorare/core/src/lib/gql';
import { Stats, statLabels } from '@sorare/core/src/lib/scoring';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';

import { GenericGame } from '@sorare/gameplay/src/components/seo/Games/Game';
import { anyGameToGenericGame } from '@sorare/gameplay/src/lib/anyGame';

import type { playerFootballGameToGeneric_game } from './__generated__/utils.graphql';

const CapitalizedLabelS = styled(LabelS)`
  &::first-letter {
    text-transform: capitalize;
  }
`;

export const playerFootballGameToGeneric = withFragments(
  ({
    game,
    onClick,
  }: {
    game: playerFootballGameToGeneric_game;
    onClick?: (id: string) => void;
  }): GenericGame => {
    const matchLiveOrPlayed = [
      GameStatus.played,
      GameStatus.playing,
    ].includesValue(game.status);
    const DNP = !game.so5Score?.playerGameStats.minsPlayed;

    const stats =
      game.so5Score?.player.position === Position.Goalkeeper
        ? game.so5Score?.positiveDecisiveStats.filter(stat =>
            ['penalties_saved', 'penalty_save', 'clean_sheet_60'].includes(
              stat.stat
            )
          )
        : [
            {
              stat: 'goals',
              statValue: game.so5Score?.playerGameStats?.goals || 0,
            },
            {
              stat: 'goal_assist',
              statValue: game.so5Score?.playerGameStats?.goalAssist || 0,
            },
          ];

    return {
      ...anyGameToGenericGame({ anyGame: game, onClick }),
      ...(matchLiveOrPlayed && game.so5Score
        ? {
            playerScore: (
              <BodyS
                as="p"
                bold
                color={
                  DNP
                    ? 'var(--c-nd-600)'
                    : findThresholdColor(
                        game.so5Score.score || 0,
                        Sport.FOOTBALL
                      ).color
                }
              >
                {DNP ? (
                  <FormattedMessage
                    {...playerGameStatusLabels.did_not_play_short}
                  />
                ) : (
                  <FormattedNumber
                    value={Math.floor(game.so5Score.score || 0)}
                    maximumFractionDigits={0}
                  />
                )}
              </BodyS>
            ),
            playerStats: !DNP && (
              <Horizontal>
                <Horizontal gap={0.5}>
                  <FontAwesomeIcon icon={faClock} />
                  <LabelS as="p">
                    {game.so5Score.playerGameStats?.minsPlayed || 0}
                  </LabelS>
                </Horizontal>
                {stats
                  .filter(stat => stat.statValue > 0)
                  .map(stat => (
                    <Tooltip
                      key={stat.stat}
                      title={
                        stat.stat in statLabels ? (
                          <CapitalizedLabelS as="p">
                            <FormattedMessage
                              {...statLabels[
                                stat.stat as keyof typeof statLabels
                              ]}
                            />
                          </CapitalizedLabelS>
                        ) : (
                          ''
                        )
                      }
                    >
                      <Horizontal key={stat.stat} gap={0.5}>
                        <StatIcon statKey={stat.stat as Stats} />
                        <LabelS as="p">{stat.statValue}</LabelS>
                      </Horizontal>
                    </Tooltip>
                  ))}
              </Horizontal>
            ),
          }
        : {}),
    };
  },
  {
    game: gql`
      fragment playerFootballGameToGeneric_game on Game {
        id
        status: statusTyped
        so5Score(playerSlug: $slug) {
          id
          score
          player {
            slug
            position
          }
          playerGameStats {
            id
            goals
            goalAssist
            minsPlayed
          }
          positiveDecisiveStats {
            stat
            statValue
          }
        }
        ...anyGameToGenericGame_anyGameInterface
      }
      ${anyGameToGenericGame.fragments.anyGameInterface}
    ` as TypedDocumentNode<playerFootballGameToGeneric_game>,
  }
);
